import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Table, Card, Space, Tag, Input, Select, Button, DatePicker, Row, Col,
  Statistic, message, Tooltip, Modal, Descriptions
} from 'antd';
import {
  ShoppingOutlined, SearchOutlined, DownloadOutlined, EyeOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined,
  CarOutlined, InboxOutlined, UserOutlined, ShopOutlined, EuroOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';
import { exportToExcel } from '../../utils/excelExport';
import BackButton from '../../components/BackButton';

const { RangePicker } = DatePicker;

const AdminOrderList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({});
  const [partners, setPartners] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [filters, setFilters] = useState({
    status: null,
    partner: null,
    dateRange: null,
    search: '',
  });

  useEffect(() => {
    loadOrders();
    loadPartners();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.partner) params.partner = filters.partner;
      if (filters.search) params.search = filters.search;
      if (filters.dateRange && filters.dateRange[0]) {
        params.date_from = filters.dateRange[0].format('YYYY-MM-DD');
        params.date_to = filters.dateRange[1].format('YYYY-MM-DD');
      }

      const response = await api.get('/admin/orders/', { params });
      setOrders(response.data.orders || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      message.error(t('admin.orders.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const loadPartners = async () => {
    try {
      const response = await api.get('/partners/admin/');
      setPartners(response.data.results || response.data || []);
    } catch (error) {
      console.error('Failed to load partners');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    loadOrders();
  };

  const clearFilters = () => {
    setFilters({
      status: null,
      partner: null,
      dateRange: null,
      search: '',
    });
  };

  const showOrderDetail = async (orderId) => {
    setDetailLoading(true);
    setDetailModalVisible(true);
    try {
      const response = await api.get(`/admin/orders/${orderId}/`);
      setSelectedOrder(response.data);
    } catch (error) {
      message.error(t('admin.orders.loadDetailFailed'));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await api.patch(`/admin/orders/${orderId}/`, { status: newStatus });
      message.success(t('admin.orders.statusUpdated'));
      loadOrders();
      if (selectedOrder && selectedOrder.id === orderId) {
        showOrderDetail(orderId);
      }
    } catch (error) {
      message.error(t('admin.orders.statusUpdateFailed'));
    }
  };

  const handleExport = () => {
    const exportData = orders.map(order => ({
      ...order,
      created_at: dayjs(order.created_at).format('DD/MM/YYYY HH:mm'),
    }));
    const exportColumns = [
      { title: 'ID', dataIndex: 'id' },
      { title: t('admin.orders.customer'), dataIndex: 'customer_name' },
      { title: t('admin.orders.customerEmail'), dataIndex: 'customer_email' },
      { title: t('admin.orders.partner'), dataIndex: 'partner_name' },
      { title: t('admin.orders.items'), dataIndex: 'item_count' },
      { title: t('admin.orders.subtotal'), dataIndex: 'subtotal' },
      { title: t('admin.orders.shipping'), dataIndex: 'shipping_cost' },
      { title: t('admin.orders.platformFee'), dataIndex: 'platform_fee' },
      { title: t('admin.orders.total'), dataIndex: 'total_amount' },
      { title: t('admin.orders.status'), dataIndex: 'status' },
      { title: t('admin.orders.createdAt'), dataIndex: 'created_at' },
    ];
    exportToExcel(exportData, exportColumns, 'orders', t('admin.orders.title'));
    message.success(t('admin.orders.exportSuccess'));
  };

  const getStatusColor = (status) => {
    const colors = {
      PENDING: 'orange',
      CONFIRMED: 'blue',
      PROCESSING: 'cyan',
      SHIPPED: 'purple',
      DELIVERED: 'green',
      CANCELLED: 'red'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      PENDING: <ClockCircleOutlined />,
      CONFIRMED: <CheckCircleOutlined />,
      PROCESSING: <InboxOutlined />,
      SHIPPED: <CarOutlined />,
      DELIVERED: <CheckCircleOutlined />,
      CANCELLED: <CloseCircleOutlined />
    };
    return icons[status] || <ClockCircleOutlined />;
  };

  // Auto-reload when filters change via stat cards
  const [autoReload, setAutoReload] = useState(false);
  useEffect(() => {
    if (autoReload) {
      loadOrders();
      setAutoReload(false);
    }
  }, [filters, autoReload]);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      fixed: 'left',
      render: (id) => <strong>#{id}</strong>,
    },
    {
      title: t('admin.orders.customer'),
      key: 'customer',
      width: 180,
      ellipsis: true,
      render: (_, record) => (
        <div style={{ overflow: 'hidden' }}>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}><UserOutlined /> {record.customer_name}</div>
          <div style={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{record.customer_email}</div>
        </div>
      ),
    },
    {
      title: t('admin.orders.partner'),
      key: 'partner',
      width: 150,
      ellipsis: true,
      render: (_, record) => (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <ShopOutlined /> {record.partner_name}
        </div>
      ),
    },
    {
      title: t('admin.orders.items'),
      dataIndex: 'item_count',
      key: 'item_count',
      width: 70,
      align: 'center',
    },
    {
      title: t('admin.orders.total'),
      key: 'total',
      width: 100,
      render: (_, record) => (
        <strong style={{ color: '#1890ff' }}>
          {record.total_amount}
        </strong>
      ),
    },
    {
      title: t('admin.orders.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {t(`admin.orders.statuses.${status.toLowerCase()}`)}
        </Tag>
      ),
    },
    {
      title: t('admin.orders.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (date) => dayjs(date).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title={t('common.view')}>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => showOrderDetail(record.id)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <BackButton to="/admin/dashboard" label={t('admin.backToDashboard')} />

      {/* Summary Stats - Clickable */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Tooltip title={t('admin.orders.clickToShowAll')}>
            <Card
              hoverable
              onClick={() => {
                setFilters({ status: null, partner: null, dateRange: null, search: '' });
                setAutoReload(true);
              }}
              style={{ cursor: 'pointer' }}
            >
              <Statistic
                title={t('admin.orders.totalOrders')}
                value={summary.total || 0}
                prefix={<ShoppingOutlined />}
              />
            </Card>
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Tooltip title={t('admin.orders.clickToShowPending')}>
            <Card
              hoverable
              onClick={() => {
                setFilters({ status: 'PENDING', partner: null, dateRange: null, search: '' });
                setAutoReload(true);
              }}
              style={{ cursor: 'pointer', border: filters.status === 'PENDING' ? '2px solid #faad14' : undefined }}
            >
              <Statistic
                title={t('admin.orders.pendingOrders')}
                value={summary.pending || 0}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Tooltip title={t('admin.orders.clickToShowDelivered')}>
            <Card
              hoverable
              onClick={() => {
                setFilters({ status: 'DELIVERED', partner: null, dateRange: null, search: '' });
                setAutoReload(true);
              }}
              style={{ cursor: 'pointer', border: filters.status === 'DELIVERED' ? '2px solid #52c41a' : undefined }}
            >
              <Statistic
                title={t('admin.orders.deliveredOrders')}
                value={summary.delivered || 0}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('admin.orders.totalRevenue')}
              value={summary.total_revenue || 0}
              prefix={<EuroOutlined />}
              precision={2}
              suffix=""
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={
          <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
            <ShoppingOutlined style={{ marginRight: 8 }} />
            {t('admin.orders.title')}
          </span>
        }
      >
        {/* Filters */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={4}>
            <Input
              placeholder={t('admin.orders.searchPlaceholder')}
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onPressEnter={applyFilters}
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Select
              placeholder={t('admin.orders.filterByStatus')}
              style={{ width: '100%' }}
              allowClear
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
            >
              <Select.Option value="PENDING">{t('admin.orders.statuses.pending')}</Select.Option>
              <Select.Option value="CONFIRMED">{t('admin.orders.statuses.confirmed')}</Select.Option>
              <Select.Option value="PROCESSING">{t('admin.orders.statuses.processing')}</Select.Option>
              <Select.Option value="SHIPPED">{t('admin.orders.statuses.shipped')}</Select.Option>
              <Select.Option value="DELIVERED">{t('admin.orders.statuses.delivered')}</Select.Option>
              <Select.Option value="CANCELLED">{t('admin.orders.statuses.cancelled')}</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder={t('admin.orders.filterByPartner')}
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="children"
              value={filters.partner}
              onChange={(value) => handleFilterChange('partner', value)}
            >
              {partners.map(p => (
                <Select.Option key={p.id} value={p.id}>
                  {p.business_name || p.store_name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={5}>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.dateRange}
              onChange={(dates) => handleFilterChange('dateRange', dates)}
              placeholder={[t('admin.orders.dateFrom'), t('admin.orders.dateTo')]}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Space>
              <Button type="primary" onClick={applyFilters}>
                {t('admin.applyFilters')}
              </Button>
              <Button onClick={clearFilters}>
                {t('admin.clearFilters')}
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExport}
                disabled={!orders.length}
              >
                {t('admin.exportExcel')}
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title={`${t('admin.orders.orderDetails')} #${selectedOrder?.id}`}
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedOrder(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            {t('common.close')}
          </Button>
        ]}
        width={700}
        loading={detailLoading}
      >
        {selectedOrder && (
          <div>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label={t('admin.orders.orderId')}>
                #{selectedOrder.id}
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.orders.status')}>
                <Select
                  value={selectedOrder.status}
                  onChange={(value) => handleStatusChange(selectedOrder.id, value)}
                  style={{ width: 150 }}
                >
                  <Select.Option value="PENDING">{t('admin.orders.statuses.pending')}</Select.Option>
                  <Select.Option value="CONFIRMED">{t('admin.orders.statuses.confirmed')}</Select.Option>
                  <Select.Option value="PROCESSING">{t('admin.orders.statuses.processing')}</Select.Option>
                  <Select.Option value="SHIPPED">{t('admin.orders.statuses.shipped')}</Select.Option>
                  <Select.Option value="DELIVERED">{t('admin.orders.statuses.delivered')}</Select.Option>
                  <Select.Option value="CANCELLED">{t('admin.orders.statuses.cancelled')}</Select.Option>
                </Select>
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.orders.customer')}>
                {selectedOrder.customer?.name}
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.orders.customerEmail')}>
                {selectedOrder.customer?.email}
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.orders.partner')}>
                {selectedOrder.partner?.name}
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.orders.createdAt')}>
                {dayjs(selectedOrder.created_at).format('DD/MM/YYYY HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.orders.shippingAddress')} span={2}>
                {selectedOrder.shipping_address}
              </Descriptions.Item>
            </Descriptions>

            <h4 style={{ marginTop: 16 }}>{t('admin.orders.orderItems')}</h4>
            <Table
              dataSource={selectedOrder.items || []}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                { title: t('admin.orders.product'), dataIndex: 'product_name' },
                { title: t('admin.orders.variant'), dataIndex: 'variant_name' },
                { title: t('admin.orders.quantity'), dataIndex: 'quantity', width: 80 },
                { title: t('admin.orders.unitPrice'), dataIndex: 'unit_price', width: 100 },
                { title: t('admin.orders.subtotal'), dataIndex: 'subtotal', width: 100 },
              ]}
            />

            <Descriptions bordered size="small" column={2} style={{ marginTop: 16 }}>
              <Descriptions.Item label={t('admin.orders.subtotal')}>
                {selectedOrder.subtotal}
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.orders.shipping')}>
                {selectedOrder.shipping_cost}
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.orders.platformFee')}>
                {selectedOrder.platform_fee}
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.orders.total')}>
                <strong style={{ color: '#1890ff', fontSize: 16 }}>
                  {selectedOrder.total_amount}
                </strong>
              </Descriptions.Item>
            </Descriptions>

            {/* NGO Donations - Admin Only */}
            <h4 style={{ marginTop: 16 }}>{t('admin.orders.donations')}</h4>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label={t('admin.orders.animapDonation')}>
                <span style={{ color: '#1890ff' }}>
                  €{selectedOrder.animap_ngo_donation_amount || '0.00'}
                </span>
                {selectedOrder.animap_ngo_name && (
                  <span style={{ marginLeft: 8, color: '#666' }}>
                    → {selectedOrder.animap_ngo_name}
                  </span>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.orders.partnerDonation')}>
                <span style={{ color: '#52c41a' }}>
                  €{selectedOrder.partner_ngo_donation_amount || '0.00'}
                </span>
                {selectedOrder.partner_ngo_name && (
                  <span style={{ marginLeft: 8, color: '#666' }}>
                    → {selectedOrder.partner_ngo_name}
                  </span>
                )}
              </Descriptions.Item>
            </Descriptions>

            {selectedOrder.notes && (
              <div style={{ marginTop: 16 }}>
                <h4>{t('admin.orders.notes')}</h4>
                <p>{selectedOrder.notes}</p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminOrderList;
